import { useCallback, useEffect, useRef, useState } from "react";

import type {
  GameModel,
  PregameViewModel,
  RemoteTimestamp,
  ScheduleModel,
} from "../shared/types/contracts";
import type { SchedulePatch } from "./teamTypes";

export interface ScheduleProjectionHandle {
  acknowledgement: Promise<void> | null;
}

interface ScheduleProjection {
  actionId: string;
  scheduleId: string;
  expectedSchedule: ScheduleModel;
  expectedPregame: PregameViewModel | null;
  expectedGame: Pick<GameModel, "id" | "scheduledDate" | "timeSlot"> | null;
  basePregameRevision: number;
  baseGameUpdatedAt: number;
  committedRevision: number | null;
  responsePregameSequence: number | null;
  responseGameSequence: number | null;
  scheduleAcknowledged: boolean;
  pregameAcknowledged: boolean;
  gameAcknowledged: boolean;
  acknowledgement: Promise<void>;
  resolveAcknowledgement: () => void;
}

interface OptimisticScheduleOptions {
  scopeKey: string;
  scheduleGames: ScheduleModel[];
  authoritativePregame: PregameViewModel;
  authoritativePregameUpdatedAt: RemoteTimestamp | undefined;
  renderedPregame: PregameViewModel;
  authoritativeGame: GameModel | null | undefined;
  renderedGame: GameModel | null | undefined;
  activeGameId: string | null;
}

export interface OptimisticScheduleController {
  scheduleGames: ScheduleModel[];
  pregame: PregameViewModel;
  rawGame: GameModel | null | undefined;
  projectUpdate: (
    actionId: string,
    scheduleId: string,
    patch: SchedulePatch,
  ) => ScheduleProjectionHandle;
  markCommitted: (actionId: string, revision: number) => void;
  reject: (actionId: string) => void;
  pregameBarrier: () => {
    actionIds: string[];
    acknowledgement: Promise<void>;
  };
}

function sameBeerDuty(
  left: ScheduleModel["beerDuty"] | null,
  right: ScheduleModel["beerDuty"] | null,
): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

function toMillis(timestamp: RemoteTimestamp | undefined): number {
  if (typeof timestamp === "number") return timestamp;
  if (timestamp instanceof Date) return timestamp.getTime();
  if (
    typeof timestamp === "object" &&
    timestamp !== null &&
    "toMillis" in timestamp &&
    typeof timestamp.toMillis === "function"
  ) {
    return timestamp.toMillis();
  }
  return 0;
}

function pregameMatches(
  current: PregameViewModel,
  expected: PregameViewModel,
): boolean {
  return (
    current.selectedScheduleId === expected.selectedScheduleId &&
    current.oppName === expected.oppName &&
    current.isHome === expected.isHome &&
    current.scheduledDate === expected.scheduledDate &&
    current.timeSlot === expected.timeSlot &&
    current.field === expected.field &&
    sameBeerDuty(current.beerDuty, expected.beerDuty)
  );
}

function gameMatches(
  current: GameModel,
  expected: Pick<GameModel, "id" | "scheduledDate" | "timeSlot">,
): boolean {
  return (
    current.id === expected.id &&
    (current.scheduledDate ?? null) === (expected.scheduledDate ?? null) &&
    (current.timeSlot ?? null) === (expected.timeSlot ?? null)
  );
}

function applySchedulePatch(
  schedule: ScheduleModel,
  patch: SchedulePatch,
): ScheduleModel {
  return {
    ...schedule,
    ...patch,
    ...(patch.opponentName === undefined
      ? {}
      : { opponentName: patch.opponentName.trim() }),
  };
}

function scheduleToPregame(
  pregame: PregameViewModel,
  schedule: ScheduleModel,
): PregameViewModel {
  return {
    ...pregame,
    selectedScheduleId: schedule.id,
    oppName: schedule.opponentName,
    isHome: schedule.isHome,
    scheduledDate: schedule.scheduledDate,
    timeSlot: schedule.timeSlot,
    field: schedule.field,
    beerDuty: schedule.beerDuty,
  };
}

function applyExpectedPregameFields(
  current: PregameViewModel,
  expected: PregameViewModel,
): PregameViewModel {
  return {
    ...current,
    selectedScheduleId: expected.selectedScheduleId,
    oppName: expected.oppName,
    isHome: expected.isHome,
    scheduledDate: expected.scheduledDate,
    timeSlot: expected.timeSlot,
    field: expected.field,
    beerDuty: expected.beerDuty,
  };
}

export function useOptimisticSchedule({
  scopeKey,
  scheduleGames: authoritativeScheduleGames,
  authoritativePregame,
  authoritativePregameUpdatedAt,
  renderedPregame,
  authoritativeGame,
  renderedGame,
  activeGameId,
}: OptimisticScheduleOptions): OptimisticScheduleController {
  const [, render] = useState(0);
  const projectionsRef = useRef<ScheduleProjection[]>([]);
  const scopeRef = useRef(scopeKey);
  const authoritativeSchedulesRef = useRef(authoritativeScheduleGames);
  const authoritativePregameRef = useRef(authoritativePregame);
  const authoritativePregameUpdatedAtRef = useRef(
    authoritativePregameUpdatedAt,
  );
  const authoritativeGameRef = useRef(authoritativeGame);
  const activeGameIdRef = useRef(activeGameId);
  const pregameSnapshotRef = useRef({
    value: authoritativePregame,
    sequence: 0,
  });
  const gameSnapshotRef = useRef({ value: authoritativeGame, sequence: 0 });
  authoritativeSchedulesRef.current = authoritativeScheduleGames;
  authoritativePregameRef.current = authoritativePregame;
  authoritativePregameUpdatedAtRef.current = authoritativePregameUpdatedAt;
  authoritativeGameRef.current = authoritativeGame;
  activeGameIdRef.current = activeGameId;
  if (pregameSnapshotRef.current.value !== authoritativePregame) {
    pregameSnapshotRef.current = {
      value: authoritativePregame,
      sequence: pregameSnapshotRef.current.sequence + 1,
    };
  }
  if (gameSnapshotRef.current.value !== authoritativeGame) {
    gameSnapshotRef.current = {
      value: authoritativeGame,
      sequence: gameSnapshotRef.current.sequence + 1,
    };
  }

  useEffect(() => {
    if (scopeRef.current === scopeKey) return;
    scopeRef.current = scopeKey;
    const pending = projectionsRef.current;
    projectionsRef.current = [];
    pending.forEach((projection) => projection.resolveAcknowledgement());
    render((value) => value + 1);
  }, [scopeKey]);

  const reconcile = useCallback(() => {
    const schedules = authoritativeSchedulesRef.current;
    const pregame = authoritativePregameRef.current;
    const game = authoritativeGameRef.current;
    const pregameSequence = pregameSnapshotRef.current.sequence;
    const gameSequence = gameSnapshotRef.current.sequence;
    let changed = false;
    const remaining: ScheduleProjection[] = [];
    for (const projection of projectionsRef.current) {
      const schedule = schedules.find(
        (candidate) => candidate.id === projection.scheduleId,
      );
      if (
        projection.committedRevision !== null &&
        schedule &&
        schedule.revision >= projection.committedRevision
      ) {
        if (!projection.scheduleAcknowledged) changed = true;
        projection.scheduleAcknowledged = true;
      } else if (projection.committedRevision !== null && !schedule) {
        if (!projection.scheduleAcknowledged) changed = true;
        projection.scheduleAcknowledged = true;
      }
      if (!projection.expectedPregame) {
        projection.pregameAcknowledged = true;
      } else if (projection.committedRevision !== null) {
        const exact = pregameMatches(pregame, projection.expectedPregame);
        const normalized =
          projection.scheduleAcknowledged &&
          !!schedule &&
          pregameMatches(
            pregame,
            scheduleToPregame(projection.expectedPregame, schedule),
          );
        const scheduleUpdatedAt = toMillis(schedule?.updatedAt);
        const superseded =
          projection.scheduleAcknowledged &&
          scheduleUpdatedAt > 0 &&
          toMillis(authoritativePregameUpdatedAtRef.current) >
            scheduleUpdatedAt;
        const legacySuperseded =
          scheduleUpdatedAt === 0 &&
          pregame.revision > projection.basePregameRevision &&
          projection.responsePregameSequence !== null &&
          pregameSequence > projection.responsePregameSequence;
        if (exact || normalized || superseded || legacySuperseded) {
          if (!projection.pregameAcknowledged) changed = true;
          projection.pregameAcknowledged = true;
        }
      }
      if (!projection.expectedGame) {
        projection.gameAcknowledged = true;
      } else if (
        projection.committedRevision !== null &&
        activeGameIdRef.current !== projection.expectedGame.id
      ) {
        if (!projection.gameAcknowledged) changed = true;
        projection.gameAcknowledged = true;
      } else if (projection.committedRevision !== null && game) {
        const exact = gameMatches(game, projection.expectedGame);
        const normalized =
          projection.scheduleAcknowledged &&
          !!schedule &&
          gameMatches(game, {
            id: projection.expectedGame.id,
            scheduledDate: schedule.scheduledDate,
            timeSlot: schedule.timeSlot,
          });
        const scheduleUpdatedAt = toMillis(schedule?.updatedAt);
        const superseded =
          projection.scheduleAcknowledged &&
          scheduleUpdatedAt > 0 &&
          toMillis(game.updatedAt) > scheduleUpdatedAt;
        const legacySuperseded =
          scheduleUpdatedAt === 0 &&
          toMillis(game.updatedAt) > projection.baseGameUpdatedAt &&
          projection.responseGameSequence !== null &&
          gameSequence > projection.responseGameSequence;
        if (exact || normalized || superseded || legacySuperseded) {
          if (!projection.gameAcknowledged) changed = true;
          projection.gameAcknowledged = true;
        }
      }
      if (
        projection.scheduleAcknowledged &&
        projection.pregameAcknowledged &&
        projection.gameAcknowledged
      ) {
        projection.resolveAcknowledgement();
        changed = true;
      } else {
        remaining.push(projection);
      }
    }
    if (remaining.length !== projectionsRef.current.length) {
      projectionsRef.current = remaining;
    }
    if (changed) render((value) => value + 1);
  }, []);

  useEffect(reconcile, [
    authoritativeScheduleGames,
    authoritativePregame,
    authoritativePregameUpdatedAt,
    authoritativeGame,
    activeGameId,
    reconcile,
  ]);

  let scheduleGames = authoritativeScheduleGames.map((entry) => ({ ...entry }));
  let pregame = renderedPregame;
  let rawGame = renderedGame;
  for (const projection of projectionsRef.current) {
    if (!projection.scheduleAcknowledged) {
      scheduleGames = scheduleGames.map((entry) =>
        entry.id === projection.scheduleId
          ? { ...projection.expectedSchedule, revision: entry.revision }
          : entry,
      );
    }
    if (
      !projection.pregameAcknowledged &&
      projection.expectedPregame &&
      pregame.selectedScheduleId === projection.scheduleId
    ) {
      pregame = applyExpectedPregameFields(pregame, projection.expectedPregame);
    }
    if (!projection.gameAcknowledged && projection.expectedGame && rawGame) {
      rawGame = { ...rawGame, ...projection.expectedGame };
    }
  }
  const renderedPregameRef = useRef(pregame);
  const renderedGameRef = useRef(rawGame);
  renderedPregameRef.current = pregame;
  renderedGameRef.current = rawGame;

  const projectUpdate = useCallback(
    (
      actionId: string,
      scheduleId: string,
      patch: SchedulePatch,
    ): ScheduleProjectionHandle => {
      let schedule = authoritativeSchedulesRef.current.find(
        (candidate) => candidate.id === scheduleId,
      );
      if (!schedule) return { acknowledgement: null };
      let currentPregame = renderedPregameRef.current;
      let currentGame = renderedGameRef.current;
      for (const pending of projectionsRef.current) {
        if (
          pending.scheduleId === scheduleId &&
          !pending.scheduleAcknowledged
        ) {
          schedule = pending.expectedSchedule;
        }
        if (
          !pending.pregameAcknowledged &&
          pending.expectedPregame &&
          currentPregame.selectedScheduleId === pending.scheduleId
        ) {
          currentPregame = applyExpectedPregameFields(
            currentPregame,
            pending.expectedPregame,
          );
        }
        if (!pending.gameAcknowledged && pending.expectedGame && currentGame) {
          currentGame = { ...currentGame, ...pending.expectedGame };
        }
      }
      const expectedSchedule = applySchedulePatch(schedule, patch);
      const expectedPregame =
        currentPregame.selectedScheduleId === scheduleId
          ? scheduleToPregame(currentPregame, expectedSchedule)
          : null;
      const expectedGame =
        schedule.status === "active" &&
        schedule.gameId &&
        currentGame?.id === schedule.gameId
          ? {
              id: currentGame.id,
              scheduledDate: expectedSchedule.scheduledDate,
              timeSlot: expectedSchedule.timeSlot,
            }
          : null;
      let resolveAcknowledgement!: () => void;
      const acknowledgement = new Promise<void>((resolve) => {
        resolveAcknowledgement = resolve;
      });
      const projection: ScheduleProjection = {
        actionId,
        scheduleId,
        expectedSchedule,
        expectedPregame,
        expectedGame,
        basePregameRevision: authoritativePregameRef.current.revision,
        baseGameUpdatedAt: toMillis(authoritativeGameRef.current?.updatedAt),
        committedRevision: null,
        responsePregameSequence: null,
        responseGameSequence: null,
        scheduleAcknowledged: false,
        pregameAcknowledged: expectedPregame === null,
        gameAcknowledged: expectedGame === null,
        acknowledgement,
        resolveAcknowledgement,
      };
      projectionsRef.current = [...projectionsRef.current, projection];
      render((value) => value + 1);
      return { acknowledgement };
    },
    [],
  );

  const markCommitted = useCallback(
    (actionId: string, revision: number) => {
      const projection = projectionsRef.current.find(
        (candidate) => candidate.actionId === actionId,
      );
      if (!projection) return;
      projection.committedRevision = revision;
      projection.responsePregameSequence = pregameSnapshotRef.current.sequence;
      projection.responseGameSequence = gameSnapshotRef.current.sequence;
      reconcile();
    },
    [reconcile],
  );

  const reject = useCallback((actionId: string) => {
    const projection = projectionsRef.current.find(
      (candidate) => candidate.actionId === actionId,
    );
    if (!projection) return;
    projectionsRef.current = projectionsRef.current.filter(
      (candidate) => candidate.actionId !== actionId,
    );
    projection.resolveAcknowledgement();
    render((value) => value + 1);
  }, []);

  const pregameBarrier = useCallback(() => {
    const pending = projectionsRef.current.filter(
      (projection) =>
        projection.expectedPregame && !projection.pregameAcknowledged,
    );
    return {
      actionIds: pending.map((projection) => projection.actionId),
      acknowledgement: Promise.all(
        pending.map((projection) => projection.acknowledgement),
      ).then(() => undefined),
    };
  }, []);

  return {
    scheduleGames,
    pregame,
    rawGame,
    projectUpdate,
    markCommitted,
    reject,
    pregameBarrier,
  };
}
